import React, { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import {
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
  FunnelIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const PAGE_SIZE = 25
const STATUS_LABELS = { open: 'Open', reviewing: 'Reviewing', closed: 'Closed' }
const STATUS_COLORS = {
  open: 'bg-blue-50 text-blue-700',
  reviewing: 'bg-amber-50 text-amber-700',
  closed: 'bg-emerald-50 text-emerald-700',
}

function formatWhen(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return String(value)
  }
}

function joinList(val) {
  if (Array.isArray(val)) return val.filter(Boolean).join(', ') || '—'
  return '—'
}

function formatOptionList(ids, items) {
  if (Array.isArray(items) && items.length) {
    return items.map((i) => i.label || i.id).filter(Boolean).join(', ') || '—'
  }
  return joinList(ids)
}

function DetailField({ label, children, className = '', fullWidth = false }) {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-3.5 shadow-sm ${
        fullWidth ? 'sm:col-span-2' : ''
      } ${className}`}
    >
      <dt className="text-xs font-semibold uppercase tracking-wide text-teal-700">{label}</dt>
      <dd className="mt-2 text-sm leading-relaxed text-gray-900">{children}</dd>
    </div>
  )
}

function patronDetailLine(selected) {
  const patron = selected.patron
  if (patron?.display_name || patron?.name) {
    const kind = patron.kind ? String(patron.kind).replace(/^./, (c) => c.toUpperCase()) : null
    const membership = patron.membership_number || selected.membership_number
    const parts = [kind, patron.display_name || patron.name].filter(Boolean)
    return (
      <>
        {parts.join(' — ')}
        {membership ? <span className="text-gray-500"> ({membership})</span> : null}
      </>
    )
  }
  const kind = selected.patron_kind
    ? String(selected.patron_kind).replace(/^./, (c) => c.toUpperCase())
    : null
  const name = selected.patron_name || '—'
  const membership = selected.membership_number
  return (
    <>
      {[kind, name].filter((x) => x && x !== '—').join(' — ') || '—'}
      {membership ? <span className="text-gray-500"> ({membership})</span> : null}
    </>
  )
}

export default function IncidentRegisterMonitoring() {
  const { can } = useAuth()
  const canCreate =
    can('compliance-safe/incident-register', 'manage') || can('mobile/ir', 'create')
  const [searchParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [filter, setFilter] = useState({ status: '', category_id: '', dateFrom: '', dateTo: '', member_id: '' })
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [reviewNotes, setReviewNotes] = useState('')
  const [updating, setUpdating] = useState(false)
  useEffect(() => {
    const memberFromUrl = searchParams.get('member_id')
    if (memberFromUrl) {
      setFilter((prev) => ({ ...prev, member_id: memberFromUrl }))
      setPage(1)
    }
  }, [searchParams])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (page - 1) * PAGE_SIZE
      let url = `/api/mobile/ir-reports?limit=${PAGE_SIZE}&offset=${offset}`
      if (filter.member_id) url += `&member_id=${encodeURIComponent(filter.member_id)}`
      if (filter.status) url += `&status=${encodeURIComponent(filter.status)}`
      if (filter.category_id) url += `&category_id=${encodeURIComponent(filter.category_id)}`
      if (filter.dateFrom) url += `&dateFrom=${encodeURIComponent(filter.dateFrom)}`
      if (filter.dateTo) url += `&dateTo=${encodeURIComponent(filter.dateTo)}`
      const res = await authFetch(getLocalApiUrl(url))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRows(Array.isArray(data.data) ? data.data : [])
      setTotal(Number(data.total) || 0)
    } catch (e) {
      toast.error(e.message || 'Failed to load incident register reports')
    } finally {
      setLoading(false)
    }
  }, [filter, page])

  useEffect(() => {
    load()
  }, [load])

  const updateFilter = (patch) => {
    setFilter((f) => ({ ...f, ...patch }))
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const openDetail = async (row) => {
    setSelected(row)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/mobile/ir-reports/${encodeURIComponent(row.id)}`))
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.data) setSelected({ ...row, ...data.data })
    } catch (_) {
      /* keep list row */
    }
  }

  const recordReport = async (id) => {
    setUpdating(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/mobile/ir-reports/${encodeURIComponent(id)}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'closed',
          review_notes: reviewNotes.trim() || 'Recorded — no further action required',
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Failed to update report')
      toast.success('Incident register report recorded')
      setSelected(null)
      setReviewNotes('')
      await load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-teal-700 p-3 shadow-lg">
            <ClipboardDocumentListIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Incident Register</h1>
            <p className="text-sm text-gray-600">Responsible Conduct of Gambling (RCG) — separate from UAR (AML)</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canCreate && (
            <Link
              to="/compliance-safe/incident-register/capture"
              className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-800"
            >
              <PlusIcon className="h-4 w-4" />
              New incident
            </Link>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FunnelIcon className="h-4 w-4 text-gray-400" />
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={filter.status}
          onChange={(e) => updateFilter({ status: e.target.value })}
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="reviewing">Reviewing</option>
          <option value="closed">Closed</option>
        </select>
        <input
          type="date"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={filter.dateFrom}
          onChange={(e) => updateFilter({ dateFrom: e.target.value })}
        />
        <input
          type="date"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={filter.dateTo}
          onChange={(e) => updateFilter({ dateTo: e.target.value })}
        />
        <input
          type="text"
          placeholder="Member / patron"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={filter.member_id}
          onChange={(e) => updateFilter({ member_id: e.target.value })}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-700">IR #</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Category</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Patron</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Venue</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Submitted</th>
              <th className="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && !rows.length ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : !rows.length ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No incident register reports found</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-900">{row.id}</td>
                <td className="px-4 py-3 text-gray-800">
                  <span className="text-xs font-medium text-gray-500 mr-1">{row.report_category}</span>
                  {row.selection_label || row.selection_id}
                </td>
                <td className="px-4 py-3 text-gray-700">{row.patron_name || row.membership_number || '—'}</td>
                <td className="px-4 py-3 text-gray-700">{row.venue_id}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status] || STATUS_COLORS.open}`}>
                    {STATUS_LABELS[row.status] || row.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{formatWhen(row.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button type="button" onClick={() => openDetail(row)} className="text-teal-700 hover:underline inline-flex items-center gap-1">
                    <EyeIcon className="h-4 w-4" /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded border px-3 py-1 disabled:opacity-40">Previous</button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border px-3 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Incident Register {selected.id}</h2>
                <p className="text-sm text-gray-500">{formatWhen(selected.created_at)}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-800">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-5">
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailField label="Report category">{selected.report_category || '—'}</DetailField>
                <DetailField label="Selection">
                  {selected.selection_label || selected.selection_id || '—'}
                </DetailField>
                <DetailField label="Sub-selections" fullWidth>
                  {joinList(selected.sub_selection_labels || selected.warning_sign_labels)}
                </DetailField>
                <DetailField label="Patron">{patronDetailLine(selected)}</DetailField>
                <DetailField label="Location">{selected.location_detail || '—'}</DetailField>
                <DetailField label="Assessment" fullWidth>
                  <p className="whitespace-pre-wrap">{selected.assessment_text || '—'}</p>
                </DetailField>
                <DetailField label="Welfare check">
                  {selected.welfare_check_performed == null
                    ? '—'
                    : selected.welfare_check_performed
                      ? 'Yes'
                      : 'No'}
                  {selected.welfare_check_notes ? (
                    <p className="mt-2 text-gray-600">{selected.welfare_check_notes}</p>
                  ) : null}
                </DetailField>
                <DetailField label="Assistance">
                  {formatOptionList(selected.assistance_provided, selected.assistance_provided_items)}
                </DetailField>
                {selected.assistance_notes && (
                  <DetailField label="Assistance notes" fullWidth>{selected.assistance_notes}</DetailField>
                )}
                <DetailField label="Stage of change">
                  {formatOptionList(selected.stage_of_change, selected.stage_of_change_items)}
                </DetailField>
                <DetailField label="Further actions">
                  {formatOptionList(selected.further_actions, selected.further_actions_items)}
                </DetailField>
                {selected.submitted_by?.name && (
                  <DetailField label="Submitted by">
                    {selected.submitted_by.name}
                    {selected.submitted_by.role_label ? (
                      <span className="text-gray-500"> · {selected.submitted_by.role_label}</span>
                    ) : null}
                  </DetailField>
                )}
                {selected.print_break_voucher && (
                  <DetailField label="Break voucher">
                    <span className="font-medium text-amber-700">Break in Play voucher requested</span>
                  </DetailField>
                )}
                {selected.review_notes && (
                  <DetailField label="Review notes" fullWidth>{selected.review_notes}</DetailField>
                )}
              </dl>

              {selected.status !== 'closed' && selected.selection_id !== 'no_rcg_incident' && selected.category_id !== 'no_rcg_incident' && (
                <div className="mt-6 border-t border-gray-200 pt-5 space-y-3">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-teal-700">
                    Review notes
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-900 focus:border-teal-600 focus:ring-teal-600"
                    rows={3}
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                  />
                  <button
                    type="button"
                    disabled={updating}
                    onClick={() => recordReport(selected.id)}
                    className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50"
                  >
                    {updating ? 'Saving…' : 'Mark as recorded'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
